"use client";

import { motion } from "motion/react";
import { BackButton } from "@/components/ui/back-button";
import { Button } from "@/components/ui/button";
import Link from "@/components/ui/link";
import { HiiveLogoIcon } from "@/svgs/hiive-logo-icon";

export default function NotFound() {
  return (
    <div className="relative flex min-h-[100dvh] w-full flex-col items-center justify-center overflow-hidden bg-background p-6 text-center font-sans text-foreground">
      {/* Background - Clean & Premium */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            rotate: [0, 360],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 40,
            repeat: Number.POSITIVE_INFINITY,
            ease: "linear",
          }}
          className="absolute -top-[50%] -left-[50%] h-[200vh] w-[200vh] rounded-full bg-[radial-gradient(circle_at_center,rgba(var(--primary),0.03)_0%,transparent_50%)]"
        />
      </div>

      <div className="relative z-10 flex w-full max-w-lg flex-col items-center">
        {/* Logo Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative mb-12"
        >
          {/* Floating Logo - Just floating in brand color */}
          <motion.div
            animate={{
              y: [-8, 8, -8],
              rotate: [-2, 2, -2],
            }}
            transition={{
              duration: 6,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
            }}
            className="relative z-10 drop-shadow-2xl"
          >
            <HiiveLogoIcon width={160} height={160} />
          </motion.div>
        </motion.div>

        {/* Text Content */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="space-y-4 px-4"
        >
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Lost in the Hive?
          </h2>
          <p className="mx-auto max-w-sm text-base leading-relaxed text-muted-foreground">
            We couldn't find the page you're looking for. It might have buzzed
            off or never existed.
          </p>
        </motion.div>

        {/* Actions - Full Width & Clean */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-12 flex w-full max-w-sm flex-col gap-3"
        >
          <Button
            asChild
            size="lg"
            className="w-full rounded-full text-base font-medium shadow-sm transition-all hover:shadow-md"
          >
            <Link href="/">Return Home</Link>
          </Button>

          <BackButton
            fallbackHref="/"
            label="Go Back"
            showLabel
            variant="outline"
            size="lg"
            className="w-full rounded-full border-border transition-all hover:bg-surface-alt hover:text-foreground"
          />
        </motion.div>
      </div>

      {/* 404 Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 flex items-center gap-2 font-mono text-[10px] tracking-widest text-muted-foreground/30 uppercase"
      >
        <span>Status</span>
        <span className="h-px w-3 bg-border/50" />
        <span>404 Not Found</span>
      </motion.div>
    </div>
  );
}
